// src/pages/RegisterPage.js

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AccountBalance as AccountBalanceIcon,
  Person,
  Email,
  Lock,
  Business
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import { apiService } from '../services/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = useMemo(() => String(searchParams.get('invitation_token') || searchParams.get('token') || '').trim(), [searchParams]);
  const isInvitationFlow = Boolean(invitationToken);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    firma: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState(null);

  useEffect(() => {
    if (!invitationToken) {
      setInvitation(null);
      return;
    }

    let cancelled = false;

    const loadInvitation = async () => {
      try {
        const result = await apiService.get(`/api/auth/invitation-preview/${encodeURIComponent(invitationToken)}`);
        if (cancelled) return;

        setInvitation(result?.invitation || null);
        const profile = result?.profile || null;
        const suggestedName = [profile?.vorname, profile?.nachname].filter(Boolean).join(' ').trim();
        const istPlatzhalter = Boolean(result?.invitation?.is_platzhalter);
        setFormData((prev) => ({
          ...prev,
          // Platzhalter-Einladung: E-Mail frei wählbar, nicht vorbefüllen
          email: istPlatzhalter ? prev.email : (result?.invitation?.email || prev.email),
          name: suggestedName || prev.name,
        }));
      } catch (err) {
        if (cancelled) return;
        setInvitation(null);
        setError(err?.message || 'Die Einladung konnte nicht geladen werden.');
      }
    };

    loadInvitation();
    return () => {
      cancelled = true;
    };
  }, [invitationToken]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    // Passwort-Komplexitätsvalidierung
    if (!/[A-Z]/.test(formData.password)) {
      setError('Passwort muss mindestens einen Großbuchstaben enthalten');
      return;
    }

    if (!/[a-z]/.test(formData.password)) {
      setError('Passwort muss mindestens einen Kleinbuchstaben enthalten');
      return;
    }

    if (!/[0-9]/.test(formData.password)) {
      setError('Passwort muss mindestens eine Ziffer enthalten');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(getApiUrl('/api/auth/register'), {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        firma: formData.firma,
        invitation_token: invitationToken || undefined
      });

      if (response.data.success) {
        // Erfolgreiche Registrierung - zeige Erfolgsmelding
        setSuccess(true);
        setError('');
      }
    } catch (err) {
      console.error('Registration error:', err);
      
      // Backend-Validierungsfehler (express-validator)
      if (err.response?.data?.details && Array.isArray(err.response.data.details)) {
        const errorMessages = err.response.data.details.map(detail => detail.msg).join(', ');
        setError(errorMessages);
      } else {
        setError(err.response?.data?.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Zeige Erfolgsseite nach Registrierung
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f2942 0%, #1a365d 50%, #2d3748 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              p: 5,
              borderRadius: 4,
              border: '2px solid rgba(0, 188, 212, 0.2)',
              background: 'linear-gradient(to bottom, #ffffff, #f8fafc)',
              textAlign: 'center'
            }}
          >
            <AccountBalanceIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a365d', mb: 2 }}>
              {invitation ? 'Mitarbeiter-Zugang aktiviert! 🎉' : 'Registrierung erfolgreich! 🎉'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
              {invitation
                ? <>Ihr Mitarbeiter-Zugang für <strong>{formData.email}</strong> ist jetzt aktiv.</>
                : <>Wir haben Ihnen eine Bestätigungs-E-Mail an <strong>{formData.email}</strong> gesendet.</>}
            </Typography>
            {invitation ? (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                Einladung für {invitation.email} · Rolle {invitation.role || 'zeiterfasser'}
              </Alert>
            ) : null}
            
            {invitation ? (
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                Ihr Mitarbeiterzugang ist bereit. Sie können sich jetzt anmelden.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    📧 Bitte bestätigen Sie Ihre E-Mail-Adresse
                  </Typography>
                  <Typography variant="body2">
                    Klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren. 
                    Der Link ist 24 Stunden gültig.
                  </Typography>
                </Alert>

                <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    ⚠️ Keine E-Mail erhalten?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                    • Überprüfen Sie Ihren Spam-Ordner<br />
                    • Warten Sie einige Minuten<br />
                    • Kontaktieren Sie unseren Support
                  </Typography>
                </Box>
              </>
            )}

            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#1a365d',
                py: 2,
                px: 6,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#0f2942'
                }
              }}
            >
              Zum Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f2942 0%, #1a365d 50%, #2d3748 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 5,
            borderRadius: 4,
            border: '2px solid rgba(0, 188, 212, 0.2)',
            background: 'linear-gradient(to bottom, #ffffff, #f8fafc)'
          }}
        >
          {/* Logo & Titel */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: '#00bcd4', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a365d', mb: 1 }}>
              {isInvitationFlow ? 'Passwort setzen' : 'Konto erstellen'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {isInvitationFlow
                ? 'Vervollständigen Sie den eingeladenen Zugang mit einem neuen Passwort.'
                : 'Starten Sie noch heute mit Ihrer Immobilienverwaltung'}
            </Typography>
            {invitation ? (
              <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                Einladung für {invitation.email} · Rolle {invitation.role || 'zeiterfasser'}
              </Alert>
            ) : null}
            {!isInvitationFlow ? (
              <Chip
                label="25€ Startguthaben geschenkt"
                sx={{
                  mt: 2,
                  bgcolor: '#fbbf24',
                  color: '#1a365d',
                  fontWeight: 700,
                  fontSize: '0.875rem'
                }}
              />
            ) : null}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isInvitationFlow && Boolean(formData.name)}
              helperText={isInvitationFlow ? 'Der Name wurde aus der Einladung übernommen.' : ''}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: '#1a365d' }} />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="E-Mail-Adresse"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isInvitationFlow && Boolean(formData.email)}
              helperText={isInvitationFlow ? 'Die E-Mail wurde aus der Einladung übernommen.' : ''}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#1a365d' }} />
                  </InputAdornment>
                )
              }}
            />

            {!isInvitationFlow ? (
              <TextField
                fullWidth
                label="Firma (optional)"
                name="firma"
                value={formData.firma}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business sx={{ color: '#1a365d' }} />
                    </InputAdornment>
                  )
                }}
              />
            ) : null}

            <TextField
              fullWidth
              label="Passwort"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#1a365d' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Passwort-Anforderungen */}
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#64748b' }}>
                Passwort-Anforderungen:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: formData.password.length >= 8 ? '#10b981' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {formData.password.length >= 8 ? '✓' : '○'} Mindestens 8 Zeichen
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: /[A-Z]/.test(formData.password) ? '#10b981' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {/[A-Z]/.test(formData.password) ? '✓' : '○'} Mindestens ein Großbuchstabe
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: /[a-z]/.test(formData.password) ? '#10b981' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {/[a-z]/.test(formData.password) ? '✓' : '○'} Mindestens ein Kleinbuchstabe
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: /[0-9]/.test(formData.password) ? '#10b981' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {/[0-9]/.test(formData.password) ? '✓' : '○'} Mindestens eine Ziffer
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Passwort bestätigen"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#1a365d' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#1a365d',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                mb: 2,
                '&:hover': {
                  bgcolor: '#0f2942'
                }
              }}
            >
              {loading ? 'Wird gespeichert...' : (isInvitationFlow ? 'Passwort setzen und Zugang aktivieren' : 'Jetzt registrieren')}
            </Button>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                ODER
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Haben Sie bereits ein Konto?{' '}
                <Link
                  to="/login"
                  style={{
                    color: '#00bcd4',
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                >
                  Jetzt anmelden
                </Link>
              </Typography>
            </Box>
          </form>

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textAlign: 'center' }}>
              Mit der Registrierung stimmen Sie unseren{' '}
              <Link to="/agb" style={{ color: '#00bcd4', textDecoration: 'none' }}>
                AGB
              </Link>{' '}
              und der{' '}
              <Link to="/datenschutz" style={{ color: '#00bcd4', textDecoration: 'none' }}>
                Datenschutzerklärung
              </Link>{' '}
              zu.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default RegisterPage;
